/* eslint-disable react/no-unescaped-entities */
import { FC, RefObject, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { msg, Trans } from "@lingui/macro";
import { Trans as TransComponent } from "@lingui/react";
import { Heading, Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { useTheme, Theme } from "@emotion/react";
import { locale, storeConfig } from "src/config/app-config";
import { ArrowDown } from "src/icons/ArrowDown";
import { Button } from "src/atoms/Button";
import { Checkbox } from "src/atoms/Checkbox";
import { CTPrice } from "src/atoms/CTPrice";
import { CartWarning } from "src/atoms/CartWarning";
import { Done } from "src/icons/Done";
import { TrustpilotCustom } from "src/molecules/TrustpilotBox";
import { CheckoutOrderSummarySkeleton } from "src/molecules/CheckoutOrderSummarySkeleton";
import { SummaryCustomLineItem } from "src/molecules/SummaryCustomLineItem";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { getCTAttribute } from "src/utils/ct/attributeHelper";
import { getCartItemsCount } from "src/utils/ct/cart-helper";
import { getCartItemAvailability } from "src/utils/ct/cartItemAvailabilityHelper";
import { getTotalSaved, lineItemsTotalPrice } from "src/utils/ct/totalPrice";
import { Cart } from "src/types/ctgraphql.d";
import { Miscellaneous } from "src/types/graphql.d";
import { isShippingPriceFree } from "src/utils/ct/shippingMethodPrice";
import { isOptedIn } from "src/utils/opt-in";
import { PaymentMethodsInfo } from "src/molecules/PaymentMethodsInfo";
import { getAppliedDiscounts } from "src/utils/appliedDiscounts";
import { OrderSummaryDiscounts } from "src/molecules/OrderSummaryDiscounts";
import {
  CheckoutOrderSummaryWrapper,
  PaymentMethodsWrapper,
} from "./CheckoutOrderSummary.styled";
import { ApplePayButton } from "../ApplePayButton";

const LEGAL_LINK_PROPS = {
  style: { textDecoration: "underline" },
  target: "_blank",
  rel: "noreferrer",
};

const LEGAL_CONSENT = msg`summary.legal_consent`;
const termsHref = getI18nRoute({ route: "/terms-and-conditions", locale });
const privacyHref = getI18nRoute({ route: "/privacy-statement", locale });

const CheckoutOrderSummary: FC<{
  loading?: boolean;
  cart: Cart | null;
  miscellaneous: Miscellaneous | undefined;
  step: 1 | 2;
  buttonLabel: string;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  onClick:
    | ((optInShown: boolean, isOptIn: boolean, applePayToken?: string) => void)
    | (() => Promise<any>);
  payPalRef?: RefObject<HTMLDivElement> | null;
  isPayPalSelected?: boolean;
  isApplePaySelected?: boolean;
  paymentId?: string;
}> = ({
  loading,
  cart,
  miscellaneous,
  step,
  buttonLabel,
  buttonDisabled,
  buttonLoading,
  onClick,
  payPalRef,
  isPayPalSelected,
  isApplePaySelected,
  paymentId,
}): JSX.Element => {
  const { data: session } = useSession();
  const isUserOptIn = isOptedIn(session?.user.opt_in);

  const theme: Theme = useTheme();
  const router = useRouter();
  const isNativeApp = router.query.hasOwnProperty("app");
  const optInShown = step === 2 && !isUserOptIn && locale !== "en-us";

  const [lineItemsListOpened, setLineItemsListOpened] =
    useState<boolean>(false);
  const [isOptIn, setIsOptIn] = useState<boolean>(true);

  const totalSaved = useMemo(() => getTotalSaved(cart, storeConfig), [cart]);

  const isPayPalButtonAvailable = typeof isPayPalSelected === "boolean";

  const hasUnsellableItems = useMemo(
    () =>
      cart?.lineItems.some((item) =>
        item.variant?.attributesRaw.some(
          (attr) => attr.name === "sellable" && !attr.value
        )
      ) || false,
    [cart?.lineItems]
  );

  const isItemWarning = useMemo(
    () =>
      cart?.lineItems.reduce((isWarning, item) => {
        const { isItemExceedsAvailability, isItemOutOfStock } =
          getCartItemAvailability(item);

        return isWarning || isItemExceedsAvailability || isItemOutOfStock;
      }, false) || false,
    [cart?.lineItems]
  );

  const appliedDiscounts = useMemo(
    () => (cart ? getAppliedDiscounts(cart) : []),
    [cart]
  );
  const totalColor = appliedDiscounts.length
    ? theme.colors.semantic.green
    : undefined;

  if (!cart || loading) {
    return <CheckoutOrderSummarySkeleton width={390} />;
  }

  return (
    <CheckoutOrderSummaryWrapper>
      {isItemWarning || hasUnsellableItems ? (
        <CartWarning>
          <Trans>Sorry, one item is no longer available.</Trans>{" "}
          {!isNativeApp && (
            <Link
              href="/cart"
              style={{ textDecoration: "underline" }}
              prefetch={false}
            >
              <Trans>Go to shopping cart.</Trans>
            </Link>
          )}
        </CartWarning>
      ) : null}

      <Box
        mt={isItemWarning || hasUnsellableItems ? 4 : 0}
        py={5}
        px={[3, 3, 3, 5]}
        backgroundColor="tone.sand"
        sx={{
          borderRadius: "4px",
        }}
      >
        <Flex flexDirection="row">
          <Box>
            <Heading as="h4" variant="h4">
              <Trans>Order summary</Trans>
            </Heading>
          </Box>
        </Flex>

        <Box
          mt={3}
          fontSize={0}
          sx={{
            lineHeight: `${theme.fontSizes[2]}px`,
          }}
        >
          <Flex justifyContent="space-between" fontWeight={600}>
            <Box
              data-testid="checkout-order-summary-total-item-review"
              as="button"
              onClick={() => setLineItemsListOpened(!lineItemsListOpened)}
              sx={{
                background: "none",
                border: "none",
                color: "tone.black",
                fontWeight: 600,
                ":hover": {
                  cursor: "pointer",
                },
              }}
            >
              <Trans>Total items</Trans> ({getCartItemsCount(cart.lineItems)})
              <Box display="inline-block" ml={2}>
                <Box
                  sx={
                    lineItemsListOpened ? { transform: "rotate(180deg)" } : {}
                  }
                >
                  <ArrowDown
                    height="8px"
                    width="8px"
                    color={theme.colors.tone.black}
                  />
                </Box>
              </Box>
            </Box>
            <Box data-testid="checkout-order-summary-total-items-price">
              <CTPrice price={lineItemsTotalPrice(cart.lineItems)} />
            </Box>
          </Flex>

          {lineItemsListOpened && (
            <Flex mt={2} mb={3}>
              <Box color="tone.anthracite" flex={1} pl={4}>
                <ul>
                  {cart.lineItems.map(({ id, price, variant }) => {
                    const sizeValue = getCTAttribute(
                      "size_value",
                      variant?.attributesRaw
                    ) as string;
                    return (
                      <li key={id}>
                        <Flex justifyContent="space-between">
                          <Flex>
                            <Text
                              data-testid="checkout-order-summary-item-name"
                              maxWidth={200}
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {
                                getCTAttribute(
                                  "name",
                                  variant?.attributesRaw
                                ) as string
                              }
                            </Text>
                            <Text data-testid="checkout-order-summary-item-size">
                              {sizeValue ? `, ${sizeValue}` : ""}
                            </Text>
                          </Flex>
                          <Box data-testid="checkout-order-summary-item-price">
                            <CTPrice price={price.value} />
                          </Box>
                        </Flex>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            </Flex>
          )}

          {cart?.shippingInfo?.price && (
            <Flex mt={2} justifyContent="space-between">
              <Box>
                <Trans>summary.shipping_fee</Trans>
              </Box>
              <Box>
                {isShippingPriceFree(cart) ? (
                  <span className="free-shipping">
                    <Trans>Free</Trans>
                  </span>
                ) : (
                  <CTPrice
                    data-testid="checkout-order-summary-shipping-price"
                    price={
                      cart.shippingInfo.discountedPrice?.value ||
                      cart.shippingInfo.price
                    }
                  />
                )}
              </Box>
            </Flex>
          )}

          {cart?.customLineItems.map((customLineItem, index) => (
            <Flex key={index} mt={2}>
              <SummaryCustomLineItem
                dataTestIdPrefix="checkout-order-summary-"
                customLineItem={customLineItem}
              />
            </Flex>
          ))}

          {cart?.shippingAddress?.country === "US" && (
            <Flex mt={2} justifyContent="space-between">
              <Box>
                <Trans>State taxes</Trans>
              </Box>
              <Box>
                {cart.taxedPrice?.totalTax ? (
                  <CTPrice price={cart.taxedPrice?.totalTax} />
                ) : (
                  <Trans>To be calculated</Trans>
                )}
              </Box>
            </Flex>
          )}

          <OrderSummaryDiscounts
            discounts={appliedDiscounts}
            dataTestIdPrefix="checkout-order-summary-"
          />
        </Box>

        {(cart.taxedPrice?.totalGross || cart.totalPrice) && (
          <Text
            mt={4}
            fontFamily="PlusJakartaSans"
            fontSize={[18, 18, 20]}
            fontWeight={700}
            lineHeight={`${theme.fontSizes[3]}px`}
            as="div"
          >
            <Flex justifyContent="space-between">
              <Box color={totalColor}>
                <Trans>Total</Trans>
              </Box>
              <Box
                data-testid="checkout-order-summary-total-amount"
                color={totalColor}
              >
                <CTPrice
                  price={cart.taxedPrice?.totalGross || cart.totalPrice}
                />
              </Box>
            </Flex>
          </Text>
        )}

        {totalSaved && (
          <Flex
            mt={2}
            justifyContent="space-between"
            sx={{
              lineHeight: `${theme.fontSizes[2]}px`,
            }}
          >
            <Box>
              <Text fontSize={0} fontWeight={600}>
                <Trans>Total saved</Trans>
              </Text>
            </Box>
            <Box data-testid="checkout-order-summary-total-saved">
              <Text fontSize={0} fontWeight={600}>
                <CTPrice price={totalSaved} />
              </Text>
            </Box>
          </Flex>
        )}

        {totalSaved && (
          <Box mt={1}>
            <Text variant="body.source.regular12" color="tone.black">
              <Trans>Compared to the recommended retail price</Trans>
            </Text>
          </Box>
        )}

        {optInShown && (
          <Box mt={4}>
            <Checkbox
              alignInput="start"
              checked={!isOptIn}
              onChange={() => setIsOptIn(!isOptIn)}
            >
              <Text
                variant="body.source.regular16"
                sx={{ whiteSpace: "initial" }}
                as="span"
              >
                <Trans>
                  I wish to not receive emails on special offers, newest brands
                  and latest trends.
                </Trans>
              </Text>
            </Checkbox>
          </Box>
        )}

        {(!isPayPalButtonAvailable ||
          (isPayPalSelected === false && isApplePaySelected === false)) && (
          <Box mt={4}>
            <Button
              data-testid="checkout-order-summary-submit-button"
              variant="primary"
              fullWidth
              disabled={
                !!buttonDisabled ||
                buttonLoading ||
                isItemWarning ||
                hasUnsellableItems
              }
              loading={buttonLoading}
              onClick={() => void onClick(optInShown, isOptIn)}
            >
              {buttonLabel}
            </Button>
          </Box>
        )}

        <Box
          mt={4}
          display={isApplePaySelected ? "block" : "none"}
          sx={
            isItemWarning || hasUnsellableItems
              ? {
                  pointerEvents: "none",
                  opacity: 0.5,
                }
              : {}
          }
        >
          <ApplePayButton
            paymentId={paymentId}
            label={buttonLabel}
            buttonLoading={buttonLoading}
            onClickPay={(applePayToken: string) =>
              void onClick(optInShown, isOptIn, applePayToken)
            }
          ></ApplePayButton>
        </Box>

        <Box
          mt={5}
          mb="-16px"
          display={isPayPalSelected ? "block" : "none"}
          sx={
            isItemWarning || hasUnsellableItems
              ? {
                  pointerEvents: "none",
                  opacity: 0.5,
                }
              : {}
          }
        >
          <div ref={payPalRef}></div>
        </Box>

        {step === 2 ? (
          <Text
            as="p"
            mt={isPayPalSelected ? 5 : 3}
            fontSize={theme.fontSizes.fontSize12}
            lineHeight={theme.space.space16}
            textAlign="center"
            color="tone.black"
          >
            <TransComponent
              id={LEGAL_CONSENT.id}
              message={LEGAL_CONSENT.message}
              components={{
                0: <a href={termsHref} {...LEGAL_LINK_PROPS} />,
                1: <a href={privacyHref} {...LEGAL_LINK_PROPS} />,
              }}
            />
          </Text>
        ) : null}
      </Box>

      <PaymentMethodsWrapper>
        <PaymentMethodsInfo />
      </PaymentMethodsWrapper>

      <Box display={["none", "none", "none", "initial"]} py={4} px={5}>
        <Text
          as="div"
          fontSize={0}
          fontWeight="600"
          lineHeight={`${theme.fontSizes[2]}px`}
        >
          {miscellaneous?.pdp.shipping && (
            <Box mt={2} ml={1}>
              <Done
                width="16px"
                height="20px"
                color={theme.colors.semantic.green}
                style={{
                  verticalAlign: "bottom",
                }}
              />
              <Text as="span" ml={2}>
                {miscellaneous.pdp.shipping}
              </Text>
            </Box>
          )}
          {miscellaneous?.pdp.delivery && (
            <Box mt={2} ml={1}>
              <Done
                width="16px"
                height="20px"
                color={theme.colors.semantic.green}
                style={{
                  verticalAlign: "bottom",
                }}
              />
              <Text as="span" ml={2}>
                {miscellaneous.pdp.delivery}
              </Text>
            </Box>
          )}
          {miscellaneous?.pdp.returns && (
            <Box mt={2} ml={1}>
              <Done
                width="16px"
                height="20px"
                color={theme.colors.semantic.green}
                style={{
                  verticalAlign: "bottom",
                }}
              />
              <Text as="span" ml={2}>
                {miscellaneous.pdp.returns}
              </Text>
            </Box>
          )}
          <Box mt={4} ml={1}>
            <TrustpilotCustom
              data-testid="checkout-order-summary-trustpilot-link"
              locale={locale}
              color="black"
              sx={{
                justifyContent: "start",
              }}
            />
          </Box>
        </Text>
      </Box>
    </CheckoutOrderSummaryWrapper>
  );
};

export default CheckoutOrderSummary;
